import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Plus, Trash2, X, Edit2, Upload, ArrowLeft, AlertTriangle } from 'lucide-react';
import type { Guest, CreateGuestInput, UpdateGuestInput } from '@peles/shared';
import {
  useGuests,
  useGuestStats,
  useCreateGuest,
  useUpdateGuest,
  useDeleteGuest,
  useUpdateRsvp,
  useGuestFamilyGroups,
  useBulkUpdateGuests,
  useBulkDeleteGuests,
} from '../hooks/useGuests';
import { useEventTableCapacities } from '../hooks/useFloorPlans';
import { useEvent } from '../hooks/useEvents';
import { CsvImportWizard, MultiSelectDropdown } from '../components/common';
import { ExportSeatingMenu } from '../components/guests/ExportSeatingMenu';
import { GuestBulkActionBar } from '../components/guests/GuestBulkActionBar';
import { guestImportConfig } from '../features/guests/guestImportConfig';
import { useDialog } from '../context/DialogContext';

const RSVP_OPTIONS = ['invited', 'confirmed', 'declined', 'maybe'] as const;
const SIDE_OPTIONS = ['bride', 'groom', 'mutual', 'other'] as const;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const RSVP_FILTER_OPTIONS = RSVP_OPTIONS.map((v) => ({ value: v, label: cap(v) }));
const SIDE_FILTER_OPTIONS = SIDE_OPTIONS.map((v) => ({ value: v, label: cap(v) }));

const RSVP_BADGE_CLASSES: Record<string, string> = {
  confirmed: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800',
  maybe: 'bg-yellow-100 text-yellow-800',
  invited: 'bg-gray-100 text-gray-700',
};

const EMPTY_FORM: CreateGuestInput = {
  eventId: '',
  firstName: '',
  lastName: '',
  familyGroup: '',
  side: '',
  phone: '',
  email: '',
  rsvpStatus: 'invited',
  mealPreference: '',
  dietaryNotes: '',
  plusOne: false,
  plusOneName: '',
  tableNumber: undefined,
  seatNumber: undefined,
  maleCount: 1,
  femaleCount: 0,
  kidCount: 0,
  keepTogether: null,
  notes: '',
};

function guestPeopleCount(g: Pick<Guest, 'maleCount' | 'femaleCount' | 'kidCount'>) {
  return (g.maleCount ?? 0) + (g.femaleCount ?? 0) + (g.kidCount ?? 0);
}

export function GuestListPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { confirm } = useDialog();

  // Filters (multi-select arrays so users can pick multiple values per facet).
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSide, setFilterSide] = useState<string[]>([]);
  const [filterRsvp, setFilterRsvp] = useState<string[]>([]);
  const [filterFamily, setFilterFamily] = useState<string[]>([]);
  const [filterTable, setFilterTable] = useState<string[]>([]);

  // Panels
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingGuest, setEditingGuest] = useState<Guest | null>(null);

  // Capacity warning modal state
  const [capacityWarning, setCapacityWarning] = useState<{
    tableNumber: number;
    tableCapacity: number;
    currentOccupancy: number;
    guestPeople: number;
    onProceed: () => void;
  } | null>(null);

  // Form
  const [form, setForm] = useState<CreateGuestInput>({ ...EMPTY_FORM });

  // Bulk-selection state. Clears whenever any filter or search term changes
  // so a stale selection can't be acted on after the visible rows shift.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filterSide, filterRsvp, filterFamily, filterTable, searchTerm, eventId]);

  const filters = useMemo(
    () => ({
      eventId,
      side: filterSide.length ? filterSide : undefined,
      rsvpStatus: filterRsvp.length ? filterRsvp : undefined,
      familyGroup: filterFamily.length ? filterFamily : undefined,
    }),
    [eventId, filterSide, filterRsvp, filterFamily],
  );

  const hasActiveFilters =
    filterSide.length > 0 ||
    filterRsvp.length > 0 ||
    filterFamily.length > 0 ||
    filterTable.length > 0;

  const { data: guests = [], isLoading } = useGuests(filters);
  const { data: stats } = useGuestStats(eventId);
  // Pull the full universe of family groups from the backend so the filter
  // is visible even when no guest in the current page has one set yet, and
  // doesn't disappear when a filter narrows the visible list.
  const { data: familyGroups = [] } = useGuestFamilyGroups(eventId);
  // All event guests (no side/rsvp/family filter) used to build distinct table number options.
  const { data: allEventGuests = [] } = useGuests(eventId ? { eventId } : undefined);
  const { data: currentEvent } = useEvent(eventId ?? '');
  // Table capacities from the floor plan (tableNumber → seats)
  const { data: tableCapacities = {} } = useEventTableCapacities(eventId);
  const createGuest = useCreateGuest();
  const updateGuest = useUpdateGuest();
  const deleteGuest = useDeleteGuest();
  const updateRsvp = useUpdateRsvp();
  const bulkUpdate = useBulkUpdateGuests();
  const bulkDelete = useBulkDeleteGuests();

  const isEventScoped = !!eventId;

  // Distinct table numbers across all event guests (for filter options)
  const tableNumberOptions = useMemo(() => {
    const nums = [
      ...new Set(
        allEventGuests.filter((g) => g.tableNumber != null).map((g) => g.tableNumber as number),
      ),
    ].sort((a, b) => a - b);
    return nums.map((n) => ({ value: String(n), label: `Table ${n}` }));
  }, [allEventGuests]);

  // Filter by search term and table number locally
  const filteredGuests = useMemo(() => {
    let result = guests;
    if (filterTable.length) {
      const tableNums = filterTable.map(Number);
      result = result.filter((g) => g.tableNumber != null && tableNums.includes(g.tableNumber));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (g) =>
          g.firstName.toLowerCase().includes(term) ||
          (g.lastName && g.lastName.toLowerCase().includes(term)) ||
          (g.familyGroup && g.familyGroup.toLowerCase().includes(term)),
      );
    }
    return result;
  }, [guests, searchTerm, filterTable]);

  const filteredPeopleCount = useMemo(
    () =>
      filteredGuests.reduce(
        (sum, g) => sum + (g.maleCount ?? 0) + (g.femaleCount ?? 0) + (g.kidCount ?? 0),
        0,
      ),
    [filteredGuests],
  );

  const handleOpenAdd = useCallback(() => {
    setForm({ ...EMPTY_FORM, eventId: eventId || '' });
    setEditingGuest(null);
    setShowAddPanel(true);
  }, [eventId]);

  const handleOpenEdit = useCallback((guest: Guest) => {
    setEditingGuest(guest);
    setForm({
      eventId: guest.eventId,
      firstName: guest.firstName,
      lastName: guest.lastName || '',
      familyGroup: guest.familyGroup || '',
      side: guest.side || '',
      phone: guest.phone || '',
      email: guest.email || '',
      rsvpStatus: guest.rsvpStatus,
      mealPreference: guest.mealPreference || '',
      dietaryNotes: guest.dietaryNotes || '',
      plusOne: guest.plusOne,
      plusOneName: guest.plusOneName || '',
      tableNumber: guest.tableNumber || undefined,
      seatNumber: guest.seatNumber || undefined,
      maleCount: guest.maleCount,
      femaleCount: guest.femaleCount,
      kidCount: guest.kidCount,
      keepTogether: guest.keepTogether,
      notes: guest.notes || '',
    });
    setShowAddPanel(true);
  }, []);

  const handleClosePanel = useCallback(() => {
    setShowAddPanel(false);
    setEditingGuest(null);
    setForm({ ...EMPTY_FORM });
  }, []);

  const doSubmit = useCallback(() => {
    if (!form.firstName.trim()) return;
    const totalPeople = guestPeopleCount({
      maleCount: form.maleCount ?? 0,
      femaleCount: form.femaleCount ?? 0,
      kidCount: form.kidCount ?? 0,
    });
    if (totalPeople < 1) return;

    if (editingGuest) {
      const updates: UpdateGuestInput = {};
      if (form.firstName !== editingGuest.firstName) updates.firstName = form.firstName;
      if ((form.lastName || null) !== editingGuest.lastName)
        updates.lastName = form.lastName || null;
      if ((form.familyGroup || null) !== editingGuest.familyGroup)
        updates.familyGroup = form.familyGroup || null;
      if ((form.side || null) !== editingGuest.side) updates.side = form.side || null;
      if ((form.phone || null) !== editingGuest.phone) updates.phone = form.phone || null;
      if ((form.email || null) !== editingGuest.email) updates.email = form.email || null;
      if (form.rsvpStatus !== editingGuest.rsvpStatus) updates.rsvpStatus = form.rsvpStatus;
      if ((form.mealPreference || null) !== editingGuest.mealPreference)
        updates.mealPreference = form.mealPreference || null;
      if ((form.dietaryNotes || null) !== editingGuest.dietaryNotes)
        updates.dietaryNotes = form.dietaryNotes || null;
      if (form.plusOne !== editingGuest.plusOne) updates.plusOne = form.plusOne;
      if ((form.plusOneName || null) !== editingGuest.plusOneName)
        updates.plusOneName = form.plusOneName || null;
      if ((form.tableNumber ?? null) !== editingGuest.tableNumber)
        updates.tableNumber = form.tableNumber ?? null;
      if ((form.seatNumber ?? null) !== editingGuest.seatNumber)
        updates.seatNumber = form.seatNumber ?? null;
      if ((form.maleCount ?? 0) !== editingGuest.maleCount) updates.maleCount = form.maleCount ?? 0;
      if ((form.femaleCount ?? 0) !== editingGuest.femaleCount)
        updates.femaleCount = form.femaleCount ?? 0;
      if ((form.kidCount ?? 0) !== editingGuest.kidCount) updates.kidCount = form.kidCount ?? 0;
      if ((form.keepTogether ?? null) !== editingGuest.keepTogether)
        updates.keepTogether = form.keepTogether ?? null;
      if ((form.notes || null) !== editingGuest.notes) updates.notes = form.notes || null;

      updateGuest.mutate({ id: editingGuest.id, updates }, { onSuccess: () => handleClosePanel() });
    } else {
      createGuest.mutate(
        {
          ...form,
          lastName: form.lastName || undefined,
          familyGroup: form.familyGroup || undefined,
          side: form.side || undefined,
          phone: form.phone || undefined,
          email: form.email || undefined,
          mealPreference: form.mealPreference || undefined,
          dietaryNotes: form.dietaryNotes || undefined,
          plusOneName: form.plusOneName || undefined,
          notes: form.notes || undefined,
        },
        { onSuccess: () => handleClosePanel() },
      );
    }
  }, [form, editingGuest, createGuest, updateGuest, handleClosePanel]);

  const handleSubmit = useCallback(() => {
    if (!form.firstName.trim()) return;
    const totalPeople = guestPeopleCount({
      maleCount: form.maleCount ?? 0,
      femaleCount: form.femaleCount ?? 0,
      kidCount: form.kidCount ?? 0,
    });
    if (totalPeople < 1) return;

    // Check table capacity if a tableNumber is set and it differs from current
    const newTableNumber = form.tableNumber;
    const prevTableNumber = editingGuest?.tableNumber ?? null;
    if (newTableNumber && newTableNumber !== prevTableNumber) {
      const capacity = tableCapacities[newTableNumber];
      if (capacity != null) {
        // Sum people already at this table, excluding the guest being edited
        const currentOccupancy = allEventGuests
          .filter((g) => g.tableNumber === newTableNumber && g.id !== editingGuest?.id)
          .reduce(
            (sum, g) => sum + (g.maleCount ?? 0) + (g.femaleCount ?? 0) + (g.kidCount ?? 0),
            0,
          );
        if (currentOccupancy + totalPeople > capacity) {
          setCapacityWarning({
            tableNumber: newTableNumber,
            tableCapacity: capacity,
            currentOccupancy,
            guestPeople: totalPeople,
            onProceed: doSubmit,
          });
          return;
        }
      }
    }

    doSubmit();
  }, [form, editingGuest, doSubmit, tableCapacities, allEventGuests]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (
        await confirm({
          title: 'Delete Guest',
          message: 'Delete this guest?',
          confirmLabel: 'Delete',
        })
      ) {
        deleteGuest.mutate(id);
      }
    },
    [deleteGuest, confirm],
  );

  const handleRsvpClick = useCallback(
    (guest: Guest) => {
      const idx = RSVP_OPTIONS.indexOf(guest.rsvpStatus as (typeof RSVP_OPTIONS)[number]);
      const nextStatus = RSVP_OPTIONS[(idx + 1) % RSVP_OPTIONS.length];
      updateRsvp.mutate({ id: guest.id, input: { rsvpStatus: nextStatus } });
    },
    [updateRsvp],
  );

  // ── Bulk-selection helpers ─────────────────────────────────
  const toggleRowSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const visibleIds = useMemo(() => filteredGuests.map((g) => g.id), [filteredGuests]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (visibleIds.every((id) => next.has(id))) {
        // Deselect all currently visible rows.
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  }, [visibleIds]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const handleBulkSetRsvp = useCallback(
    (status: 'invited' | 'confirmed' | 'declined' | 'maybe') => {
      if (selectedIds.size === 0) return;
      bulkUpdate.mutate(
        { guestIds: Array.from(selectedIds), updates: { rsvpStatus: status } },
        { onSuccess: clearSelection },
      );
    },
    [bulkUpdate, clearSelection, selectedIds],
  );

  const handleBulkSetSide = useCallback(
    (side: 'bride' | 'groom' | 'mutual' | 'other' | null) => {
      if (selectedIds.size === 0) return;
      bulkUpdate.mutate(
        { guestIds: Array.from(selectedIds), updates: { side } },
        { onSuccess: clearSelection },
      );
    },
    [bulkUpdate, clearSelection, selectedIds],
  );

  const handleBulkSetFamily = useCallback(
    (group: string | null) => {
      if (selectedIds.size === 0) return;
      bulkUpdate.mutate(
        { guestIds: Array.from(selectedIds), updates: { familyGroup: group } },
        { onSuccess: clearSelection },
      );
    },
    [bulkUpdate, clearSelection, selectedIds],
  );

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    if (
      !(await confirm({
        title: 'Delete Guests',
        message: `Delete ${selectedIds.size} guest(s)? This cannot be undone.`,
        confirmLabel: 'Delete',
      }))
    )
      return;
    bulkDelete.mutate({ guestIds: Array.from(selectedIds) }, { onSuccess: clearSelection });
  }, [bulkDelete, clearSelection, selectedIds, confirm]);

  const handleBulkSetTable = useCallback(
    (tableNumber: number | null) => {
      if (selectedIds.size === 0) return;
      bulkUpdate.mutate(
        { guestIds: Array.from(selectedIds), updates: { tableNumber } },
        { onSuccess: clearSelection },
      );
    },
    [bulkUpdate, clearSelection, selectedIds],
  );

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Loading guests...</p>
      </div>
    );
  }

  return (
    <div>
      {isEventScoped && (
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
          data-testid="back-btn"
        >
          <ArrowLeft size={16} />
          {currentEvent ? `Back to ${currentEvent.name}` : 'Back to Event'}
        </button>
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEventScoped ? 'Event Guests' : 'All Guests'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5" data-testid="filtered-count">
            {filteredGuests.length} guest{filteredGuests.length !== 1 ? 's' : ''} ·{' '}
            {filteredPeopleCount} {filteredPeopleCount !== 1 ? 'people' : 'person'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isEventScoped && eventId && (
            <ExportSeatingMenu
              eventId={eventId}
              filters={{
                side: filterSide,
                rsvpStatus: filterRsvp,
                familyGroup: filterFamily,
              }}
            />
          )}
          <button
            onClick={() => setShowImport(true)}
            disabled={!isEventScoped}
            title={isEventScoped ? 'Import guests from CSV' : 'Pick an event first'}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="open-import-csv"
          >
            <Upload size={16} />
            <span className="hidden sm:inline">Import CSV</span>
          </button>
          <button
            onClick={handleOpenAdd}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Add Guest</span>
          </button>
        </div>
      </div>

      {isEventScoped && (
        <CsvImportWizard
          open={showImport}
          onClose={() => setShowImport(false)}
          config={guestImportConfig}
          scope={{ eventId }}
        />
      )}

      {/* Stats bar */}
      {isEventScoped && stats && (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-4"
          data-testid="stats-bar"
        >
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Confirmed" value={stats.confirmed} color="green" />
          <StatCard label="Declined" value={stats.declined} color="red" />
          <StatCard label="Maybe" value={stats.maybe} color="yellow" />
          <StatCard label="Bride Side" value={stats.bySide.bride} color="pink" />
          <StatCard label="Groom Side" value={stats.bySide.groom} color="blue" />
        </div>
      )}

      {/* Confirmed people breakdown (M / F / Kids) */}
      {isEventScoped && stats && (
        <div className="grid grid-cols-4 gap-3 mb-6" data-testid="confirmed-people-bar">
          <StatCard label="Confirmed People" value={stats.confirmedPeople.total} color="green" />
          <StatCard label="Male" value={stats.confirmedPeople.male} color="blue" />
          <StatCard label="Female" value={stats.confirmedPeople.female} color="pink" />
          <StatCard label="Kids" value={stats.confirmedPeople.kids} color="yellow" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="filters">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <MultiSelectDropdown
          options={SIDE_FILTER_OPTIONS}
          value={filterSide}
          onChange={setFilterSide}
          placeholder="All Sides"
          className="sm:w-48"
          data-testid="filter-side"
        />
        <MultiSelectDropdown
          options={RSVP_FILTER_OPTIONS}
          value={filterRsvp}
          onChange={setFilterRsvp}
          placeholder="All RSVP"
          className="sm:w-48"
          data-testid="filter-rsvp"
        />
        <MultiSelectDropdown
          options={familyGroups.map((g) => ({ value: g, label: g }))}
          value={filterFamily}
          onChange={setFilterFamily}
          placeholder="All Families"
          className="sm:w-56"
          searchable
          data-testid="filter-family"
        />
        {tableNumberOptions.length > 0 && (
          <MultiSelectDropdown
            options={tableNumberOptions}
            value={filterTable}
            onChange={setFilterTable}
            placeholder="All Tables"
            className="sm:w-40"
            data-testid="filter-table"
          />
        )}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setFilterSide([]);
              setFilterRsvp([]);
              setFilterFamily([]);
              setFilterTable([]);
            }}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            data-testid="filters-clear-all"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Bulk action bar — visible only when at least one row is selected. */}
      <GuestBulkActionBar
        selectedCount={selectedIds.size}
        onClear={clearSelection}
        onSetRsvp={handleBulkSetRsvp}
        onSetSide={handleBulkSetSide}
        onSetFamilyGroup={handleBulkSetFamily}
        onSetTable={handleBulkSetTable}
        onDelete={handleBulkDelete}
        busy={bulkUpdate.isPending || bulkDelete.isPending}
      />

      {/* Guest List */}
      {filteredGuests.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-gray-500">No guests yet</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 w-8">
                    <input
                      type="checkbox"
                      aria-label="Select all visible guests"
                      data-testid="bulk-select-all"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  {!isEventScoped && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Event
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Family
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Side
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    RSVP
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    People
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Meal
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Table
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Seat
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredGuests.map((guest) => (
                  <tr
                    key={guest.id}
                    className={`hover:bg-gray-50 ${selectedIds.has(guest.id) ? 'bg-primary-50' : ''}`}
                    data-testid={`guest-${guest.id}`}
                  >
                    <td className="px-4 py-3 w-8">
                      <input
                        type="checkbox"
                        aria-label={`Select ${guest.firstName} ${guest.lastName || ''}`.trim()}
                        data-testid={`bulk-select-${guest.id}`}
                        checked={selectedIds.has(guest.id)}
                        onChange={() => toggleRowSelection(guest.id)}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {guest.firstName} {guest.lastName || ''}
                      {guest.plusOne && (
                        <span className="ml-1 text-xs text-gray-400">
                          +1{guest.plusOneName ? ` (${guest.plusOneName})` : ''}
                        </span>
                      )}
                    </td>
                    {!isEventScoped && (
                      <td className="px-4 py-3 text-sm text-gray-600">{guest.eventName || '--'}</td>
                    )}
                    <td className="px-4 py-3 text-sm text-gray-600">{guest.familyGroup || '--'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                      {guest.side || '--'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleRsvpClick(guest)}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${RSVP_BADGE_CLASSES[guest.rsvpStatus] || RSVP_BADGE_CLASSES.invited}`}
                        data-testid={`rsvp-badge-${guest.id}`}
                      >
                        {guest.rsvpStatus}
                      </button>
                    </td>
                    <td
                      className="px-4 py-3 text-sm text-gray-600"
                      data-testid={`people-${guest.id}`}
                    >
                      <span className="font-medium text-gray-900">{guestPeopleCount(guest)}</span>
                      <span className="text-xs text-gray-500 ml-1">
                        (M:{guest.maleCount} F:{guest.femaleCount} K:{guest.kidCount})
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {guest.mealPreference || '--'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{guest.tableNumber ?? '--'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{guest.seatNumber ?? '--'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{guest.phone || '--'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => handleOpenEdit(guest)}
                          className="text-gray-400 hover:text-primary-600 p-1"
                          aria-label="Edit guest"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(guest.id)}
                          className="text-gray-400 hover:text-red-600 p-1"
                          aria-label="Delete guest"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filteredGuests.map((guest) => (
              <div
                key={guest.id}
                className={`bg-white rounded-lg shadow p-4 ${selectedIds.has(guest.id) ? 'ring-2 ring-primary-300' : ''}`}
                data-testid={`guest-card-${guest.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${guest.firstName} ${guest.lastName || ''}`.trim()}
                      data-testid={`bulk-select-card-${guest.id}`}
                      checked={selectedIds.has(guest.id)}
                      onChange={() => toggleRowSelection(guest.id)}
                    />
                    <span className="font-medium text-gray-900">
                      {guest.firstName} {guest.lastName || ''}
                    </span>
                  </span>
                  <button
                    onClick={() => handleRsvpClick(guest)}
                    className={`px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${RSVP_BADGE_CLASSES[guest.rsvpStatus] || RSVP_BADGE_CLASSES.invited}`}
                  >
                    {guest.rsvpStatus}
                  </button>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  {!isEventScoped && guest.eventName && <div>Event: {guest.eventName}</div>}
                  {guest.familyGroup && <div>Family: {guest.familyGroup}</div>}
                  {guest.side && <div>Side: {guest.side}</div>}
                  <div>
                    People: {guestPeopleCount(guest)} (M:{guest.maleCount} F:{guest.femaleCount} K:
                    {guest.kidCount})
                  </div>
                  {guest.phone && <div>Phone: {guest.phone}</div>}
                  {guest.tableNumber && (
                    <div>
                      Table: {guest.tableNumber}
                      {guest.seatNumber ? ` · Seat: ${guest.seatNumber}` : ''}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    onClick={() => handleOpenEdit(guest)}
                    className="text-primary-600 text-sm"
                  >
                    Edit
                  </button>
                  <button onClick={() => handleDelete(guest.id)} className="text-red-600 text-sm">
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add/Edit Panel */}
      {showAddPanel && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" data-testid="guest-panel">
          <div className="w-full max-w-md bg-white h-full overflow-auto p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">{editingGuest ? 'Edit Guest' : 'Add Guest'}</h2>
              <button
                onClick={handleClosePanel}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Close panel"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="guest-firstName"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={form.lastName || ''}
                  onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Family Group</label>
                <input
                  type="text"
                  value={form.familyGroup || ''}
                  onChange={(e) => setForm((f) => ({ ...f, familyGroup: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Side</label>
                <select
                  value={form.side || ''}
                  onChange={(e) => setForm((f) => ({ ...f, side: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select...</option>
                  {SIDE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="text"
                  value={form.phone || ''}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="text"
                  value={form.email || ''}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">RSVP Status</label>
                <select
                  value={form.rsvpStatus || 'invited'}
                  onChange={(e) => setForm((f) => ({ ...f, rsvpStatus: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  {RSVP_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meal Preference
                </label>
                <input
                  type="text"
                  value={form.mealPreference || ''}
                  onChange={(e) => setForm((f) => ({ ...f, mealPreference: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dietary Notes
                </label>
                <input
                  type="text"
                  value={form.dietaryNotes || ''}
                  onChange={(e) => setForm((f) => ({ ...f, dietaryNotes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.plusOne || false}
                  onChange={(e) => setForm((f) => ({ ...f, plusOne: e.target.checked }))}
                  id="plusOne"
                  className="rounded border-gray-300"
                />
                <label htmlFor="plusOne" className="text-sm font-medium text-gray-700">
                  Plus One
                </label>
              </div>
              {form.plusOne && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plus One Name
                  </label>
                  <input
                    type="text"
                    value={form.plusOneName || ''}
                    onChange={(e) => setForm((f) => ({ ...f, plusOneName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  People in this guest entry
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['maleCount', 'femaleCount', 'kidCount'] as const).map((field) => (
                    <div key={field}>
                      <label
                        htmlFor={`form-${field}`}
                        className="block text-xs text-gray-500 mb-0.5"
                      >
                        {field === 'maleCount'
                          ? 'Male'
                          : field === 'femaleCount'
                            ? 'Female'
                            : 'Kids'}
                      </label>
                      <input
                        id={`form-${field}`}
                        type="number"
                        min={0}
                        value={form[field] ?? 0}
                        data-testid={`guest-${field}`}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            [field]: e.target.value === '' ? 0 : parseInt(e.target.value, 10),
                          }))
                        }
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Total people in this guest entry:{' '}
                  {guestPeopleCount({
                    maleCount: form.maleCount ?? 0,
                    femaleCount: form.femaleCount ?? 0,
                    kidCount: form.kidCount ?? 0,
                  })}
                </p>
              </div>
              <div>
                <label
                  htmlFor="form-keepTogether"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Seat together at one table
                </label>
                <select
                  id="form-keepTogether"
                  value={
                    form.keepTogether === true
                      ? 'true'
                      : form.keepTogether === false
                        ? 'false'
                        : 'auto'
                  }
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      keepTogether: e.target.value === 'auto' ? null : e.target.value === 'true',
                    }))
                  }
                  data-testid="guest-keepTogether"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="auto">Use auto-assignment default</option>
                  <option value="true">Yes — keep at the same table</option>
                  <option value="false">No — may split across tables</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Table Number</label>
                <input
                  type="number"
                  value={form.tableNumber ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      tableNumber: e.target.value ? parseInt(e.target.value) : undefined,
                      // Clear seat number when table changes so backend auto-assigns
                      seatNumber: e.target.value ? f.seatNumber : undefined,
                    }))
                  }
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="guest-tableNumber"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seat Number
                  <span className="ml-1 text-xs text-gray-400">(auto-assigned if blank)</span>
                </label>
                <input
                  type="number"
                  value={form.seatNumber ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      seatNumber: e.target.value ? parseInt(e.target.value) : undefined,
                    }))
                  }
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="guest-seatNumber"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={form.notes || ''}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                />
              </div>

              {!isEventScoped && !editingGuest && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Event ID *</label>
                  <input
                    type="text"
                    value={form.eventId}
                    onChange={(e) => setForm((f) => ({ ...f, eventId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    data-testid="guest-eventId"
                  />
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleSubmit}
                disabled={
                  !form.firstName.trim() ||
                  guestPeopleCount({
                    maleCount: form.maleCount ?? 0,
                    femaleCount: form.femaleCount ?? 0,
                    kidCount: form.kidCount ?? 0,
                  }) < 1 ||
                  createGuest.isPending ||
                  updateGuest.isPending
                }
                className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {editingGuest ? 'Save' : 'Add Guest'}
              </button>
              <button
                onClick={handleClosePanel}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Capacity Warning Modal */}
      {capacityWarning && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]"
          data-testid="capacity-warning-modal"
        >
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="text-base font-semibold text-gray-900">Table Almost Full</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Table {capacityWarning.tableNumber} has {capacityWarning.tableCapacity} seats.{' '}
                  {capacityWarning.currentOccupancy} of them are already occupied. This guest party
                  has {capacityWarning.guestPeople} people, which would leave only{' '}
                  {capacityWarning.tableCapacity -
                    capacityWarning.currentOccupancy -
                    capacityWarning.guestPeople}{' '}
                  seat(s) remaining (going over by{' '}
                  {Math.max(
                    0,
                    capacityWarning.currentOccupancy +
                      capacityWarning.guestPeople -
                      capacityWarning.tableCapacity,
                  )}{' '}
                  seat(s)).
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setCapacityWarning(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                data-testid="capacity-warning-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setCapacityWarning(null);
                  capacityWarning.onProceed();
                }}
                className="px-4 py-2 bg-amber-500 text-white text-sm rounded-md hover:bg-amber-600"
                data-testid="capacity-warning-proceed"
              >
                Assign Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color?: string }) {
  const colorClasses: Record<string, string> = {
    green: 'text-green-600',
    red: 'text-red-600',
    yellow: 'text-yellow-600',
    pink: 'text-pink-600',
    blue: 'text-blue-600',
  };
  return (
    <div
      className="bg-white rounded-lg shadow px-4 py-3 text-center"
      data-testid={`stat-${label.toLowerCase().replace(' ', '-')}`}
    >
      <div className={`text-2xl font-bold ${color ? colorClasses[color] || '' : ''}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}
